// src/modals/MatchModal.js
import React, { useEffect } from 'react';
import { Heart, MessageCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from '../hooks';
import { Btn } from '../components/ui';
import { trackEvent } from '../services/analyticsService';
import PajNtaubBand from '../components/ui/PajNtaubBand';

const MatchModal = ({ isOpen, onClose, matchName, matchProfile, currentUser }) => {
  const { onBackdrop } = useModal(isOpen, onClose);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      trackEvent('match');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
      onClick={onBackdrop}
    >
      <div
        data-testid="match-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('matchModal.ariaLabel')}
        className="bg-white dark:bg-gray-900 w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl overflow-hidden animate-slide-up text-center relative max-h-[85dvh]"
      >
        <PajNtaubBand height={4} />
        {/* Close */}
        <button
          onClick={onClose}
          aria-label={t('matchModal.ariaClose')}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition z-10"
        >
          <X size={16} className="text-gray-500 dark:text-gray-400" />
        </button>

        {/* Header gradient */}
        <div className="bg-gradient-to-br from-rose-400 via-fuchsia-500 to-purple-500 pt-8 pb-12 px-6 relative overflow-hidden">
          {/* Confettis */}
          {[
            { cx: '-60px', cy: '-80px', color: 'bg-yellow-300', delay: '0s' },
            { cx: '70px', cy: '-90px', color: 'bg-pink-300', delay: '0.1s' },
            { cx: '-90px', cy: '30px', color: 'bg-blue-300', delay: '0.05s' },
            { cx: '80px', cy: '20px', color: 'bg-emerald-300', delay: '0.15s' },
            { cx: '-30px', cy: '-100px', color: 'bg-purple-300', delay: '0.08s' },
            { cx: '40px', cy: '50px', color: 'bg-rose-300', delay: '0.12s' },
          ].map((c, i) => (
            <div
              key={i}
              className={`absolute w-2 h-2 rounded-full ${c.color} animate-confetti`}
              style={{
                top: '50%',
                left: '50%',
                '--cx': c.cx,
                '--cy': c.cy,
                animationDelay: c.delay,
              }}
            />
          ))}
          <div className="text-5xl mb-3 relative">💘</div>
          <h2 className="text-base sm:text-xl font-black text-white mb-1 relative">
            {t('matchModal.title')}
          </h2>
          <p className="text-white/80 text-sm relative">{t('matchModal.subtitle')}</p>
        </div>

        {/* Avatars */}
        <div className="flex justify-center -mt-10 mb-4">
          <div className="relative">
            <img
              src={currentUser?.profilePicture}
              alt={currentUser?.name}
              className="w-20 h-20 rounded-full border-4 border-white dark:border-gray-900 shadow-lg object-cover"
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center">
              <Heart size={12} fill="white" className="text-white" />
            </div>
          </div>
          <div className="relative -ml-4">
            <img
              src={
                matchProfile?.profilePicture ||
                `https://api.dicebear.com/7.x/avataaars/svg?seed=${matchName}`
              }
              alt={matchName}
              className="w-20 h-20 rounded-full border-4 border-white dark:border-gray-900 shadow-lg object-cover"
            />
            <div className="absolute -bottom-1 -left-1 w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center">
              <Heart size={12} fill="white" className="text-white" />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6">
          <p className="text-gray-600 dark:text-gray-300 mb-5">
            <span className="font-bold text-gray-800 dark:text-gray-200">{currentUser?.name}</span>{' '}
            & <span className="font-bold text-gray-800 dark:text-gray-200">{matchName}</span>
          </p>

          <div className="flex flex-col gap-2">
            <Btn
              icon={MessageCircle}
              className="w-full"
              data-testid="match-modal-view"
              onClick={() => {
                onClose();
                navigate('/matches');
              }}
            >
              {t('matchModal.viewMatches')}
            </Btn>
            <Btn v="ghost" className="w-full" data-testid="match-modal-continue" onClick={onClose}>
              {t('matchModal.continueBtn')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchModal;
